import React, { useState } from 'react';
import { Container, Table, Button, Form, Row, Col, Card, Modal, Dropdown } from 'react-bootstrap';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import PageTitle from '../common/PageTitle/PageTitle';
import DateRangePicker from '../common/DateRangePicker/DateRangePicker';
import BrandButton from '../common/BrandButton/BrandButton';
import { FilterOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import styles from './transactions.module.scss';

interface Transaction {
  id: string;
  client: string;
  brand: string;
  transactionType: 'credits_purchased' | 'credits_spent' | 'music_subscription' | 'krosskast_player' | 'location_added' | 'zone_added';
  amount: number;
  credits?: number;
  subscriptionPlan?: string;
  billingCycle?: 'monthly' | 'annually';
  locationName?: string;
  zoneName?: string;
  parentTransaction?: string;
  charge?: string;
  invoice?: string;
  createdAt: string;
  updatedAt: string;
}


const Transactions: React.FC = () => {
  const [showNewTransactionModal, setShowNewTransactionModal] = useState(false);
  const [selectedTransactionType, setSelectedTransactionType] = useState<string>('');
  const [filters, setFilters] = useState({
    client: '',
    brand: '',
    transactionType: '',
    dateFrom: '',
    dateTo: '',
    amount: ''
  });

  // Mock data for transactions
  const transactions: Transaction[] = [
    {
      id: 'f236c48f-c062-4602-a63b-aa56121acic',
      client: 'Northstar Retail Inc.',
      brand: 'Northstar Retail',
      transactionType: 'credits_purchased',
      amount: 0,
      credits: 3,
      parentTransaction: '7e7dc972-6b6a-4891-816f-f53a4e599438',
      invoice: 'inv_northstarretail_001',
      createdAt: 'April 20, 2021 19:53',
      updatedAt: 'April 20, 2021 19:53'
    },
    {
      id: 'f03c2b81-f098-4707-8168-5409c75/21db',
      client: 'Acme Coffee Co.',
      brand: 'Acme Coffee',
      transactionType: 'credits_spent',
      amount: 0,
      credits: -1,
      parentTransaction: 'CREDITS TRANSACTION HAS NO PARENT',
      invoice: 'inv_acmecoffee_002',
      createdAt: 'January 27, 2023 21:11',
      updatedAt: 'January 27, 2023 21:11'
    },
    {
      id: 'd9261604-b486-4776-b2e2-4896 32d1346',
      client: 'Golden Operating Corp',
      brand: 'Golden Corral',
      transactionType: 'credits_purchased',
      amount: 0,
      credits: 6,
      parentTransaction: '62af3125-a954-4999-807e-b15bed21df80',
      invoice: 'inv_golden_003',
      createdAt: 'January 25, 2023 14:49',
      updatedAt: 'January 25, 2023 14:49'
    },
    {
      id: 'ch_3MUAIRL365L59t1m0BYyoQfh',
      client: 'Johnson Corp',
      brand: 'Example Artist Studios',
      transactionType: 'music_subscription',
      amount: 570.00,
      subscriptionPlan: 'Commercial Plan',
      billingCycle: 'annually',
      locationName: 'Main Studio',
      charge: '700.00',
      invoice: 'in_1li7XQL365L59t1mv1iVsmFn',
      createdAt: 'April 20, 2021 00:34',
      updatedAt: 'April 20, 2021 00:34'
    },
    {
      id: 'kk_player_001',
      client: 'John Doe Company',
      brand: 'Example Artist Studios',
      transactionType: 'krosskast_player',
      amount: 299.00,
      locationName: 'Main Studio',
      zoneName: 'Recording Room A',
      charge: '1000.00',
      invoice: 'in_1li7XQL365L59t1mv1iVsmFn',
      createdAt: 'January 24, 2023 01:08',
      updatedAt: 'January 24, 2023 01:08'
    }
  ];


  const filteredTransactions = transactions.filter(transaction => {
    if (filters.client && !transaction.client.toLowerCase().includes(filters.client.toLowerCase())) return false;
    if (filters.brand && !transaction.brand.toLowerCase().includes(filters.brand.toLowerCase())) return false;
    if (filters.transactionType && transaction.transactionType !== filters.transactionType) return false;
    if (filters.amount && transaction.amount !== parseFloat(filters.amount)) return false;
    // Add date filtering logic here
    return true;
  });

  const getTransactionTypeLabel = (type: string) => {
    const labels: { [key: string]: string } = {
      credits_purchased: 'Credits Purchased',
      credits_spent: 'Credits Spent',
      music_subscription: 'Music Subscription',
      krosskast_player: 'KrossKast Player',
      location_added: 'Location Added',
      zone_added: 'Zone Added'
    };
    return labels[type] || type;
  };

  const formatAmount = (amount: number) => {
    return amount === 0 ? '-' : `$${amount.toFixed(2)}`;
  };

  const handleSaveTransaction = () => {
    console.log('Saving transaction...');
    setShowNewTransactionModal(false);
  };


  return (
    <Container fluid>
      <Card style={{ borderRadius: 12 }}>
        <Card.Body>
          <Card.Title style={{ display: 'flex', justifyContent: 'space-between' }}>
            <PageTitle title="Transactions" />
            <BrandButton
              variant="outline"
              onClick={() => setFilters({client: '', brand: '', transactionType: '', dateFrom: '', dateTo: '', amount: ''})}
            >
              <FilterOutlined /> Reset All
            </BrandButton>
          </Card.Title>
        {/* Filter Section */}
        <div className={styles.filterSection}>
        <Row>
          <Col md={3}>
            <Form.Group>
              <Form.Label>Client</Form.Label>
              <Form.Select
                value={filters.client}
                onChange={(e) => setFilters({...filters, client: e.target.value})}
              >
                <option value="">Any client</option>
                <option value="Northstar Retail Inc.">Northstar Retail Inc.</option>
                <option value="Acme Coffee Co.">Acme Coffee Co.</option>
                <option value="Golden Operating Corp">Golden Operating Corp</option>
                <option value="Johnson Corp">Johnson Corp</option>
                <option value="John Doe Company">John Doe Company</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>Brand</Form.Label>
              <Form.Select
                value={filters.brand}
                onChange={(e) => setFilters({...filters, brand: e.target.value})}
              >
                <option value="">Any brand</option>
                <option value="Northstar Retail">Northstar Retail</option>
                <option value="Acme Coffee">Acme Coffee</option>
                <option value="Golden Corral">Golden Corral</option>
                <option value="Example Artist Studios">Example Artist Studios</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>Transactions</Form.Label>
              <Form.Select
                value={filters.transactionType}
                onChange={(e) => setFilters({...filters, transactionType: e.target.value})}
              >
                <option value="">Any transactions</option>
                <option value="credits_purchased">Credits Purchased</option>
                <option value="credits_spent">Credits Spent</option>
                <option value="music_subscription">Music Subscription</option>
                <option value="krosskast_player">KrossKast Player</option>
                <option value="location_added">Location Added</option>
                <option value="zone_added">Zone Added</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>Status</Form.Label>
              <Form.Select>
                <option value="">Any status</option>
                <option value="completed">Completed</option>
                <option value="pending">Pending</option>
                <option value="failed">Failed</option>
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>
                 <Row className="mt-3">
           <Col md={8}>
             <Form.Group>
               <Form.Label>Filter by Date Range</Form.Label>
               <DateRangePicker
                 maxDate={dayjs()}
                 onChange={(dates) => {
                   if (dates && dates[0] && dates[1]) {
                     const [startDate, endDate] = dates;
                     setFilters({
                       ...filters,
                       dateFrom: startDate.format('YYYY-MM-DD'),
                       dateTo: endDate.format('YYYY-MM-DD')
                     });
                   } else {
                     setFilters({
                       ...filters,
                       dateFrom: '',
                       dateTo: ''
                     });
                   }
                 }}
               />
             </Form.Group>
           </Col>
           <Col md={4} className="d-flex align-items-end justify-content-end">
             <div className={styles.actionButtons}>
               <Dropdown>
                 <Dropdown.Toggle 
                   variant="primary" 
                   size="sm" 
                   id="dropdown-new-transaction"
                   style={{
                     height: '42px',
                     padding: '10px 20px',
                     fontSize: '15px',
                     display: 'flex',
                     alignItems: 'center',
                     justifyContent: 'center',
                     marginTop: '0',
                     boxShadow: 'none'
                   }}
                 >
                   New Transaction
                 </Dropdown.Toggle>
                 <Dropdown.Menu>
                   <Dropdown.Item onClick={() => { setSelectedTransactionType('credits_purchased'); setShowNewTransactionModal(true); }}>
                     New Credit Transaction
                   </Dropdown.Item>
                   <Dropdown.Item onClick={() => { setSelectedTransactionType('music_subscription'); setShowNewTransactionModal(true); }}>
                     New Music Subscription
                   </Dropdown.Item>
                   <Dropdown.Item onClick={() => { setSelectedTransactionType('krosskast_player'); setShowNewTransactionModal(true); }}>
                     New KrossKast Player
                   </Dropdown.Item>
                   <Dropdown.Item onClick={() => { setSelectedTransactionType('location_added'); setShowNewTransactionModal(true); }}>
                     New Location
                   </Dropdown.Item>
                   <Dropdown.Item onClick={() => { setSelectedTransactionType('zone_added'); setShowNewTransactionModal(true); }}>
                     New Zone
                   </Dropdown.Item>
                 </Dropdown.Menu>
               </Dropdown>
             </div>
           </Col>
         </Row>
      </div>



      {/* Transactions Table */}
      <div className={styles.tableContainer}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ fontSize: 14, color: '#666' }}>
            Displaying all {filteredTransactions.length} Transactions
          </div>
        </div>

        <Table hover responsive className="legacy-row-height-table">
          <thead className={styles.tableHeader}>
            <tr>
              <th className={styles.checkboxColumn}>
                <input type="checkbox" />
              </th>
            <th>Client</th>
            <th>Brand</th>
            <th>Subscriptions</th>
            <th>Transactions</th>
            <th>Credits</th>
            <th>Amount</th>
            <th style={{ whiteSpace: 'nowrap' }}>Billing Cycle</th>
            <th>Location</th>
            <th>Zone</th>
            <th className={styles.compactColumn}>Charge</th>
            <th className={styles.compactColumn}>Invoice</th>
            <th>Created</th>
          </tr>
        </thead>
        <tbody>
          {filteredTransactions.map((transaction) => (
            <tr key={transaction.id} className={styles.tableRow}>
              <td className={styles.checkboxColumn}>
                <input type="checkbox" />
              </td>
              <td className={styles.noWrap}>{transaction.client}</td>
              <td className={styles.noWrap}>{transaction.brand}</td>
              <td className={styles.noWrap}>{transaction.subscriptionPlan || '-'}</td>
              <td className={styles.noWrap}>{getTransactionTypeLabel(transaction.transactionType)}</td>
              <td className={styles.noWrap}>{transaction.credits || '-'}</td>
              <td className={styles.noWrap}>{formatAmount(transaction.amount)}</td>
              <td className={styles.noWrap}>{transaction.billingCycle ? transaction.billingCycle.charAt(0).toUpperCase() + transaction.billingCycle.slice(1) : '-'}</td>
              <td className={styles.noWrap}>{transaction.locationName || '-'}</td>
              <td className={styles.noWrap}>{transaction.zoneName || '-'}</td>
              <td className={styles.compactColumn}>
                {transaction.charge ? (
                  <a href="#" style={{ color: '#007bff', textDecoration: 'underline' }}>
                    {transaction.charge}
                  </a>
                ) : '-'}
              </td>
              <td className={styles.compactColumn}>
                {transaction.invoice ? (
                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-start' }}>
                    <span style={{ color: '#333', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' }} title="View Invoice">👁</span>
                    <span style={{ color: '#333', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' }} title="Download Invoice">⬇</span>
                  </div>
                ) : '-'}
              </td>
              <td className={styles.noWrap}>{new Date(transaction.createdAt).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' })}</td>
            </tr>
          ))}
        </tbody>
      </Table>
      </div>

      {/* New Transaction Modal */}
      <Modal show={showNewTransactionModal} onHide={() => setShowNewTransactionModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>New Transaction</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Client</Form.Label>
                  <Form.Select>
                    <option>Select Client</option>
                    <option>Northstar Retail Inc.</option>
                    <option>Acme Coffee Co.</option>
                    <option>Golden Operating Corp</option>
                    <option>Example Artist Studios LLC</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Brand</Form.Label>
                  <Form.Select>
                    <option>Select Brand</option>
                    <option>Northstar Retail</option>
                    <option>Acme Coffee</option>
                    <option>Golden Corral</option>
                    <option>Example Artist Studios</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Amount</Form.Label>
                  <Form.Control type="number" placeholder="Enter amount" />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Location</Form.Label>
                  <Form.Control type="text" placeholder="Enter location name" />
                </Form.Group>
              </Col>
            </Row>
            {selectedTransactionType === 'credits_purchased' && (
              <Form.Group className="mb-3">
                <Form.Label>Credits</Form.Label>
                <Form.Control type="number" placeholder="Enter number of credits" />
              </Form.Group>
            )}
            {selectedTransactionType === 'music_subscription' && (
              <>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Subscription Plan</Form.Label>
                      <Form.Select>
                        <option>Exclusive Plan</option>
                        <option>Commercial Plan</option>
                        <option>Enterprise Plan</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Billing Cycle</Form.Label>
                      <Form.Select>
                        <option>Monthly</option>
                        <option>Annually</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Location</Form.Label>
                      <Form.Control type="text" placeholder="Enter location name" />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Zone</Form.Label>
                      <Form.Control type="text" placeholder="Enter zone name" />
                    </Form.Group>
                  </Col>
                </Row>
              </>
            )}
            {selectedTransactionType === 'krosskast_player' && (
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Location</Form.Label>
                    <Form.Control type="text" placeholder="Enter location name" />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Zone</Form.Label>
                    <Form.Control type="text" placeholder="Enter zone name" />
                  </Form.Group>
                </Col>
              </Row>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowNewTransactionModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveTransaction}>
            Save Transaction
          </Button>
        </Modal.Footer>
      </Modal>

        </Card.Body>
      </Card>
    </Container>
  );
};

export default withAuthenticationRequired(Transactions); 